import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PatientAssignmentControllerApi } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { useAppDispatch } from '../store/hooks';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { getPlanLockedMessage } from '../services/scenarioLockErrorService';
import { getApiErrorMessage } from '../utils/errorMessageUtils';

interface UsePatientAssignmentParams {
  locationId?: string;
  scenarioId?: string;
  treatmentPlanId?: string;
  /** Run after a patient has been added to the groups. */
  onAssigned?: () => void;
  /** Run after a patient has been taken off the groups. */
  onRemoved?: () => void;
}

export interface PatientAssignmentController {
  /**
   * @param isNewlyCreated whether the patient was created as part of this flow — a failure then
   * leaves a patient behind in the registry, which the message has to say.
   */
  assignPatientToGroups: (
    patientId: string,
    groupIds: string[],
    isNewlyCreated: boolean
  ) => Promise<void>;
  removePatientFromGroups: (patientId: string, groupIds: string[]) => Promise<void>;
}

/**
 * The drawer's two seat operations, with the error handling they need.
 *
 * Both endpoints are guarded by `@RequiresScenarioUnlocked`, so either can come back as a plan
 * lock 409 — which is what made cancelling a booking look broken after the lock was released: the
 * drawer dropped the error object and showed one generic message for a lock clash, an empty-body
 * 404 and a business 400 alike, so a user with a stale lock elsewhere had nothing to act on. The
 * holder is named where the server named them, the server's own message is preferred over the
 * fallback, and every failure reaches the console.
 */
export function usePatientAssignment({
  locationId,
  scenarioId,
  treatmentPlanId,
  onAssigned,
  onRemoved
}: UsePatientAssignmentParams): PatientAssignmentController {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const reportFailure = useCallback(
    (error: unknown, fallbackKey: string): void => {
      console.error('Patient assignment request failed:', error);
      const message =
        getPlanLockedMessage(error, t) ?? getApiErrorMessage(error, t(fallbackKey));
      dispatch(showError({ message }));
    },
    [dispatch, t]
  );

  const assignPatientToGroups = useCallback(
    async (patientId: string, groupIds: string[], isNewlyCreated: boolean): Promise<void> => {
      if (!locationId || !scenarioId || !treatmentPlanId || groupIds.length === 0) return;
      try {
        const api = new PatientAssignmentControllerApi(createApiConfiguration());
        await api.assignPatientToGroups({
          locationId,
          scenarioId,
          treatmentPlanId,
          patientId,
          interventionGroupIdsRequest: { interventionGroupIds: groupIds }
        });
        onAssigned?.();
      } catch (error) {
        reportFailure(
          error,
          isNewlyCreated ? 'error-patient-created-not-assigned' : 'error-patient-not-assigned'
        );
      }
    },
    [locationId, scenarioId, treatmentPlanId, onAssigned, reportFailure]
  );

  const removePatientFromGroups = useCallback(
    async (patientId: string, groupIds: string[]): Promise<void> => {
      if (!locationId || !scenarioId || !treatmentPlanId || groupIds.length === 0) return;
      try {
        const api = new PatientAssignmentControllerApi(createApiConfiguration());
        await api.removePatientFromGroups({
          locationId,
          scenarioId,
          treatmentPlanId,
          patientId,
          interventionGroupIdsRequest: { interventionGroupIds: groupIds }
        });
        onRemoved?.();
      } catch (error) {
        reportFailure(error, 'remove-patient-error');
      }
    },
    [locationId, scenarioId, treatmentPlanId, onRemoved, reportFailure]
  );

  return { assignPatientToGroups, removePatientFromGroups };
}
